import { createRoute, Link, redirect } from '@tanstack/react-router'
import { rootRoute } from './__root'
import { WorkspaceHeader } from '@/components/WorkspaceHeader'
import { ThemePicker } from '@/components/ThemePicker'
import { apiClient } from '@/lib/api'
import { useAuth } from '@/hooks/useAuth'
import { useEffectiveFeatures } from '@/hooks/useEffectiveFeatures'
import { usePlatform } from '@/hooks/usePlatform'
import { useUIStore, type CountInBars } from '@/stores/uiStore'
import { logoutNative } from '@/lib/nativeAuth'

/**
 * Platform-level settings page.
 *
 * Sections:
 * - **Profile** — name, email, avatar (read-only; WorkOS).
 * - **Theme** — the four visual themes, inline.
 * - **UI preferences** — count-in bars, grid toggle, dev-tier
 *   override.
 * - **Account** — current plan summary, Stripe portal link,
 *   sign-out.
 *
 * Project-scoped settings (BPM, Scrambler defaults, master FX,
 * export prefs, delete project) live at `/werkshop/settings`.
 */
export const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  beforeLoad: async () => {
    try {
      await apiClient.get('/api/v1/auth/me')
    } catch {
      throw redirect({ to: '/login' })
    }
  },
  component: SettingsPage,
})

function SettingsPage() {
  const { user } = useAuth()
  const { features, tier } = useEffectiveFeatures()
  const platform = usePlatform()
  const showGrid = useUIStore(s => s.showGrid)
  const setShowGrid = useUIStore(s => s.setShowGrid)
  const recordCountInBars = useUIStore(s => s.recordCountInBars)
  const setRecordCountInBars = useUIStore(s => s.setRecordCountInBars)
  const canvasTheme = useUIStore(s => s.canvasTheme)
  const setCanvasTheme = useUIStore(s => s.setCanvasTheme)

  const handleSignOut = () => {
    if (platform.isNative) {
      void logoutNative()
      window.location.href = '/login'
    } else {
      const origin = encodeURIComponent(window.location.origin)
      window.location.href = `/api/v1/auth/logout?origin=${origin}`
    }
  }

  return (
    <div className="min-h-[100dvh] bg-surface-base">
      <WorkspaceHeader title="Settings" />
      <main
        className={
          platform.isNative
            ? 'w-full px-3 sm:px-4 py-4 flex flex-col gap-6'
            : 'max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8 flex flex-col gap-8'
        }
      >
        <Section title="Profile" id="profile">
          {user ? (
            <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1 text-sm">
              <dt className="text-content-tertiary">Name</dt>
              <dd className="text-content-primary">{user.name ?? '—'}</dd>
              <dt className="text-content-tertiary">Email</dt>
              <dd className="text-content-primary">{user.email}</dd>
            </dl>
          ) : (
            <p className="text-sm text-content-tertiary">Loading…</p>
          )}
        </Section>

        <Section title="Theme" id="theme">
          <ThemePicker variant="menu" className="w-full" />
        </Section>

        <Section title="Appearance" id="appearance">
          <div className="flex flex-col gap-3">
            <fieldset className="flex flex-col gap-2">
              <legend className="text-sm text-content-primary font-medium">
                Bussboard canvas skin
              </legend>
              <p className="text-xs text-content-tertiary leading-relaxed">
                An optional patchbay skin for the Bussboard canvas — jack-plate
                device nodes, cable-style edges, rack-panel background. Opt-in;
                the clean theme stays the default. Follow-up updates ship the
                switchboard visuals incrementally without disrupting the clean
                theme.
              </p>
              <div className="flex items-center gap-2">
                <CanvasThemeRadio
                  label="Clean"
                  value="clean"
                  active={canvasTheme === 'clean'}
                  onSelect={() => setCanvasTheme('clean')}
                  description="Today's look — React Flow defaults with the active theme's surfaces."
                />
                <CanvasThemeRadio
                  label="Switchboard"
                  value="switchboard"
                  active={canvasTheme === 'switchboard'}
                  onSelect={() => setCanvasTheme('switchboard')}
                  description="Patchbay aesthetic — jack-plate nodes + cable edges. (Visuals roll out over subsequent updates.)"
                />
              </div>
            </fieldset>
          </div>
        </Section>

        <Section title="UI preferences" id="ui">
          <div className="flex flex-col gap-3">
            <label className="flex items-center gap-3 text-sm">
              <input
                type="checkbox"
                checked={showGrid}
                onChange={e => setShowGrid(e.target.checked)}
                className="h-4 w-4 rounded border-brd-subtle bg-surface-sunken accent-[color:var(--accent)]"
              />
              <span className="text-content-primary">Show canvas grid</span>
              <span className="text-content-tertiary text-xs">
                Toggle the background grid in the Bussboard canvas.
              </span>
            </label>
            <label className="flex items-center gap-3 text-sm">
              <span className="text-content-primary flex-shrink-0">
                Record count-in
              </span>
              <select
                value={recordCountInBars}
                onChange={e =>
                  setRecordCountInBars(Number(e.target.value) as CountInBars)
                }
                className="bg-surface-sunken border border-brd-subtle rounded px-2 py-1 text-sm text-content-primary"
              >
                <option value={0}>Off</option>
                <option value={1}>1 bar</option>
                <option value={2}>2 bars</option>
                <option value={4}>4 bars</option>
              </select>
              <span className="text-content-tertiary text-xs">
                Click-track bars before a pad recording starts.
              </span>
            </label>
          </div>
        </Section>

        <Section title="Account" id="account">
          <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1 text-sm mb-4">
            <dt className="text-content-tertiary">Plan</dt>
            <dd className="text-content-primary">{tier ?? 'free'}</dd>
            {features?.sequencerSave === true && (
              <>
                <dt className="text-content-tertiary">Projects</dt>
                <dd className="text-content-primary">
                  up to {features.maxSequencerProjects ?? '—'}
                </dd>
              </>
            )}
          </dl>
          <p className="text-xs text-content-tertiary mb-3">
            The Store lists every module and what you own. Once per-module
            checkout ships, your Personal and Commercial licenses will surface
            here too.
          </p>
          <div className="flex flex-wrap gap-2">
            <Link to="/werkshop/store" className="btn-secondary btn-sm">
              Open Store
            </Link>
            <button
              type="button"
              onClick={handleSignOut}
              className="btn-secondary btn-sm"
            >
              Sign out
            </button>
          </div>
        </Section>
      </main>
    </div>
  )
}

function Section({
  title,
  id,
  children,
}: {
  title: string
  id: string
  children: React.ReactNode
}) {
  return (
    <section
      id={id}
      aria-labelledby={`${id}-heading`}
      className="rounded-xl border border-brd-subtle bg-surface-raised p-5"
    >
      <h2
        id={`${id}-heading`}
        className="text-sm font-semibold text-content-primary mb-3"
      >
        {title}
      </h2>
      {children}
    </section>
  )
}

function CanvasThemeRadio({
  label,
  value,
  active,
  onSelect,
  description,
}: {
  label: string
  value: string
  active: boolean
  onSelect: () => void
  description: string
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={active}
      title={description}
      className={`flex-1 min-w-0 rounded-lg border p-3 text-left transition-colors ${
        active
          ? 'border-accent bg-accent-muted text-content-primary'
          : 'border-brd-subtle bg-surface-sunken text-content-secondary hover:border-brd-emphasis'
      }`}
    >
      <div className="text-xs font-mono uppercase tracking-wider text-content-tertiary">
        {value}
      </div>
      <div className="text-sm font-semibold text-content-primary mt-0.5">
        {label}
      </div>
      <div className="text-[11px] text-content-tertiary leading-relaxed mt-1">
        {description}
      </div>
    </button>
  )
}
